"use client";

import { useState, type FC } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

export type ExtendContextChoice = "compact" | "extend";

type Props = {
  open: boolean;
  /**
   * Whether the active provider supports extended mode. When false the dialog
   * shouldn't be opened — the caller is expected to skip-and-send directly. Kept
   * as a defensive prop so that, if it ever opens anyway, the extend button is
   * suppressed.
   */
  extendAvailable: boolean;
  /**
   * Called with the user's pick. The caller awaits this — and is expected to
   * fire the matching PATCH before invoking ComposerRuntime.send(). The dialog
   * stays open with the chosen button disabled until onChoice resolves.
   */
  onChoice: (choice: ExtendContextChoice) => Promise<void>;
  onCancel: () => void;
};

/**
 * Send-time gate that fires the first time a thread crosses the compaction
 * threshold. Two outcomes:
 *   - "compact" — accept compaction silently. Caller PATCHes /acknowledge-compaction
 *     so the modal doesn't re-fire on the next send.
 *   - "extend"  — switch the thread to extended-context mode (no compaction,
 *     higher ceiling, higher per-message cost). One-way.
 *
 * One-time per thread — gated by snap.compactionPromptAcknowledged upstream.
 */
export const ExtendContextModal: FC<Props> = ({ open, extendAvailable, onChoice, onCancel }) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<ExtendContextChoice | null>(null);

  const handle = async (choice: ExtendContextChoice) => {
    if (pending) return;
    setPending(choice);
    try {
      await onChoice(choice);
    } finally {
      setPending(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("thread.extendContext.title")}</DialogTitle>
          <DialogDescription>{t("thread.extendContext.body")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={pending !== null}
            onClick={() => handle("compact")}
          >
            {t("thread.extendContext.sendWithCompaction")}
          </Button>
          {extendAvailable && (
            <Button
              variant="default"
              disabled={pending !== null}
              onClick={() => handle("extend")}
            >
              {t("thread.extendContext.extend")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
